/**
 * Deduplication logic for backtest invoice rows.
 *
 * Natural key: supplierName + invoiceNumber + invoiceDate
 * When multiple rows share the same key, we keep the LAST one seen
 * (later rows in the file are treated as more recent / corrective entries).
 *
 * When merging a new upload against existing rows, we keep whichever version
 * came from the newer upload.
 */

import type { MappedRow, CalculatedRow } from "@/types/backtest";

/**
 * Build a deduplication key for a row.
 * Normalises whitespace and case so minor formatting differences don't cause
 * false misses (e.g. "Acme Corp" vs "acme corp").
 *
 * Falls back to a position-based key if all identity fields are null so that
 * blank rows are still kept rather than silently collapsed.
 */
export function rowKey(
  supplierName: string | null,
  invoiceNumber: string | null,
  invoiceDate: string | null,
  rowNumber: number
): string {
  const norm = (s: string | null) => (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
  const vendor = norm(supplierName);
  const inv = norm(invoiceNumber);
  const date = norm(invoiceDate);

  // If we have at least two identity fields, use them as the key.
  const hasVendor = vendor !== "";
  const hasInv = inv !== "";
  const hasDate = date !== "";

  if (hasVendor && hasInv) return `${vendor}||${inv}`;
  if (hasVendor && hasDate) return `${vendor}||${date}`;
  if (hasInv && hasDate) return `${inv}||${date}`;
  if (hasVendor) return `vendor:${vendor}||row:${rowNumber}`;
  if (hasInv) return `inv:${inv}||row:${rowNumber}`;

  // Last resort — use row position so every row is kept
  return `row:${rowNumber}`;
}

/** Summary of what a dedup pass found */
export interface DeduplicationResult<T> {
  /** Rows after deduplication (may be re-numbered) */
  rows: T[];
  /** Total input rows before dedup */
  inputCount: number;
  /** Rows that had a unique key (first occurrence) */
  newCount: number;
  /** Rows whose key was seen before and replaced an earlier version */
  updatedCount: number;
  /** Rows whose key was seen before but were dropped (older version) */
  duplicateCount: number;
}

/**
 * Deduplicate an array of MappedRows **within a single upload**.
 *
 * Strategy: last-write-wins — if the same invoice appears multiple times in
 * the same file, the row with the highest rowNumber wins.  The final rows are
 * returned in their original relative order (preserving the winning row's
 * position information).
 */
export function deduplicateMappedRows(rows: MappedRow[]): DeduplicationResult<MappedRow> {
  const inputCount = rows.length;
  // Map from key → latest row seen so far
  const seen = new Map<string, { row: MappedRow; index: number }>();

  let updatedCount = 0;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const key = rowKey(row.supplierName, row.invoiceNumber, row.invoiceDate, row.rowNumber);

    if (seen.has(key)) {
      // Replace with later occurrence (higher index = later in file)
      updatedCount++;
    }
    seen.set(key, { row, index: i });
  }

  // Reconstruct in original file order using the winning entries
  const winnerIndices = new Set<number>();
  for (const { index } of seen.values()) {
    winnerIndices.add(index);
  }

  const dedupedRows: MappedRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (winnerIndices.has(i)) {
      dedupedRows.push(rows[i]);
    }
  }

  const duplicateCount = inputCount - dedupedRows.length;
  const newCount = dedupedRows.length - updatedCount;

  return {
    rows: dedupedRows,
    inputCount,
    newCount,
    updatedCount,
    duplicateCount,
  };
}

/**
 * Deduplicate an array of CalculatedRows **within a single upload**.
 * Same last-write-wins strategy as deduplicateMappedRows.
 */
export function deduplicateCalculatedRows(
  rows: CalculatedRow[]
): DeduplicationResult<CalculatedRow> {
  const inputCount = rows.length;
  const seen = new Map<string, { row: CalculatedRow; index: number }>();

  let updatedCount = 0;

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const key = rowKey(row.supplierName, row.invoiceNumber, row.invoiceDate, row.rowNumber);

    if (seen.has(key)) {
      updatedCount++;
    }
    seen.set(key, { row, index: i });
  }

  const winnerIndices = new Set<number>();
  for (const { index } of seen.values()) {
    winnerIndices.add(index);
  }

  const dedupedRows: CalculatedRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    if (winnerIndices.has(i)) {
      dedupedRows.push(rows[i]);
    }
  }

  const duplicateCount = inputCount - dedupedRows.length;
  const newCount = dedupedRows.length - updatedCount;

  return {
    rows: dedupedRows,
    inputCount,
    newCount,
    updatedCount,
    duplicateCount,
  };
}

/**
 * Merge two sets of CalculatedRows: `existing` (from a prior upload) and
 * `incoming` (from the new upload).  The incoming rows win on any key
 * collision, so the result always reflects the most recently uploaded data.
 *
 * Returns:
 *   - rows: merged set, sorted by invoiceDate then supplierName
 *   - newCount: keys that only appeared in incoming
 *   - updatedCount: keys that appeared in both (incoming version kept)
 *   - duplicateCount: rows in incoming that were exact matches (unchanged)
 *   - unchangedCount: keys that only appeared in existing (kept as-is)
 */
export function mergeCalculatedRows(
  existing: CalculatedRow[],
  incoming: CalculatedRow[]
): DeduplicationResult<CalculatedRow> & { unchangedCount: number } {
  const existingMap = new Map<string, CalculatedRow>();
  for (const row of existing) {
    const key = rowKey(row.supplierName, row.invoiceNumber, row.invoiceDate, row.rowNumber);
    existingMap.set(key, row);
  }

  let newCount = 0;
  let updatedCount = 0;
  let duplicateCount = 0;
  const mergedMap = new Map<string, CalculatedRow>(existingMap);

  for (const row of incoming) {
    const key = rowKey(row.supplierName, row.invoiceNumber, row.invoiceDate, row.rowNumber);
    const prev = existingMap.get(key);

    if (!prev) {
      newCount++;
      mergedMap.set(key, row);
    } else if (JSON.stringify(prev) === JSON.stringify(row)) {
      // Identical row — count as duplicate but keep existing
      duplicateCount++;
    } else {
      // Updated — incoming version wins
      updatedCount++;
      mergedMap.set(key, row);
    }
  }

  const unchangedCount = existing.length - updatedCount - duplicateCount;
  const rows = Array.from(mergedMap.values()).sort((a, b) => {
    const dateA = a.invoiceDate ?? "";
    const dateB = b.invoiceDate ?? "";
    if (dateA !== dateB) return dateA.localeCompare(dateB);
    return (a.supplierName ?? "").localeCompare(b.supplierName ?? "");
  });

  return {
    rows,
    inputCount: incoming.length,
    newCount,
    updatedCount,
    duplicateCount,
    unchangedCount,
  };
}

/**
 * Human-readable summary of a dedup result.
 * e.g. "1,200 records: 980 new · 15 updated · 205 exact duplicates skipped"
 */
export function formatDeduplicationSummary(result: DeduplicationResult<unknown>): string {
  const parts: string[] = [];
  if (result.newCount > 0) parts.push(`${result.newCount.toLocaleString()} new`);
  if (result.updatedCount > 0) parts.push(`${result.updatedCount.toLocaleString()} updated`);
  if (result.duplicateCount > 0)
    parts.push(
      `${result.duplicateCount.toLocaleString()} exact duplicate${result.duplicateCount !== 1 ? "s" : ""} skipped`
    );
  if (parts.length === 0) return "No records";
  return parts.join(" · ");
}
